/*
 * Diaporama d'avis — un temoignage a la fois.
 *
 * Le defilement lui-meme est natif : la piste est une zone defilante avec
 * accrochage CSS. Le glissement au doigt, la molette et la navigation au
 * clavier fonctionnent donc avant que ce script soit charge, et continuent de
 * fonctionner s'il echoue.
 *
 * Le composant n'ajoute que les fleches et les puces — d'ou leur attribut
 * `hidden` dans le balisage : ce qui ne marche pas sans JavaScript ne doit pas
 * s'afficher sans lui.
 */
class AvisDiaporama extends HTMLElement {
  connectedCallback() {
    this.piste = this.querySelector('[data-piste]');
    this.commandes = this.querySelector('[data-commandes]');
    this.cartes = [...this.querySelectorAll('[data-avis]')];
    if (!this.piste || !this.commandes || this.cartes.length < 2) return;

    this.index = 0;
    this.commandes.hidden = false;

    this.querySelector('[data-precedent]').addEventListener('click', () => this.aller(this.index - 1));
    this.querySelector('[data-suivant]').addEventListener('click', () => this.aller(this.index + 1));

    // La position se calcule sur le defilement reel plutot que par seuil
    // d'intersection : avec des cartes a 46 %, deux d'entre elles depassaient
    // le seuil en meme temps et c'est la derniere declenchee qui l'emportait —
    // le deuxieme avis se trouvait actif au chargement.
    //
    // On retient donc la carte dont le bord gauche est le plus proche du bord
    // de la piste. C'est exact quel que soit le nombre de cartes visibles, et
    // insensible a l'ordre des evenements.
    this.surDefilement = () => {
      let meilleure = 0;
      let ecartMin = Infinity;
      const bord = this.piste.getBoundingClientRect().left;
      this.cartes.forEach((carte, i) => {
        const ecart = Math.abs(carte.getBoundingClientRect().left - bord);
        if (ecart < ecartMin) {
          ecartMin = ecart;
          meilleure = i;
        }
      });
      // En fin de course, le dernier avis ne peut pas venir se caler a gauche :
      // il reste de la piste derriere lui. Sans ce cas particulier, la fleche
      // suivante restait active et le dernier avis n'etait jamais annonce.
      const course = this.piste.scrollWidth - this.piste.clientWidth;
      if (course > 0 && this.piste.scrollLeft >= course - 2) {
        meilleure = this.cartes.length - 1;
      }

      if (meilleure === this.index) return;
      this.index = meilleure;
      this.rafraichir();
    };
    this.piste.addEventListener('scroll', this.surDefilement, { passive: true });

    this.rafraichir();
  }

  disconnectedCallback() {
    if (this.piste && this.surDefilement) {
      this.piste.removeEventListener('scroll', this.surDefilement);
    }
  }

  aller(cible) {
    // Bornage plutot que bouclage : un diaporama qui repart au debut sans
    // prevenir donne l'impression d'avoir rate un avis.
    const i = Math.max(0, Math.min(cible, this.cartes.length - 1));

    // On pose scrollLeft directement au lieu de passer par scrollIntoView.
    // Celui-ci raisonne en « amener a l'ecran » et son resultat dependait du
    // rembourrage de la piste : le dernier avis restait hors d'atteinte en
    // desktop. Ici la cible est exacte, et bornee a la course disponible.
    const course = this.piste.scrollWidth - this.piste.clientWidth;
    const cibleX = Math.min(this.cartes[i].offsetLeft - this.piste.offsetLeft, course);
    this.piste.scrollTo({
      left: Math.max(0, cibleX),
      behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
  }

  rafraichir() {
    // C'est le fondu qui designe l'avis courant, a la place des puces : la
    // carte suivante reste visible en clair sur le bord, ce qui annonce la
    // suite mieux qu'un rang de points.
    this.cartes.forEach((carte, i) => {
      carte.classList.toggle('avis__carte--actif', i === this.index);
    });
    this.querySelector('[data-precedent]').disabled = this.index === 0;
    this.querySelector('[data-suivant]').disabled = this.index === this.cartes.length - 1;
  }
}

if (!customElements.get('avis-diaporama')) {
  customElements.define('avis-diaporama', AvisDiaporama);
}
